import React, { memo, useState, useCallback, useMemo } from 'react';
import Loader from '@components/common/Loader/newLoaderModal';
import cn from 'classnames';
import Search from '@components/common/search';
import Fuse from 'fuse.js';
import Styles from './menuSearchModal.module.scss';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { LABEL } from '@veriables';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { addToCart, pushModifiersGroupData } from '@reducers/orders';
import { APP_CONFIG } from '@constants';
import { mapStringCartToObject, getObjectValue } from '@utility/utills';
import {
  checkRequiredModifierCount,
  renderInitialData,
  updateExcludeModifier,
  applyModFunction,
  calculateModifierAmount,
} from '../../../MenuContent/MenuContentItems/MenuItem/MenuItemHelper';

const FUSE_OPTIONS = {
  shouldSort: true,
  includeMatches: true,
  threshold: 0.15,
  
  minMatchCharLength: 2,
  includeScore: true,
  ignoreLocation: true,
  keys: [
    { name: "menuItem.name", weight: 1.0 },
  ],
};

const OrderMenuSearchModal = (props) => {
  const {
    isOpen,
    setSearchModalOpen,
    customClass,
    customClass2,
    loading,
    style,
    serverAppTheme,
    browserOrTablet,
    setOpenKeyboard,
    openKeyboard,
    menusList,
    menuIndex,
    menuCardList,
    menuItemsHashTable,
    modifierGroupsHashTable,
    modifiersHashTable,
  } = props;

  const navigate = useNavigate();

  const [query, updateQuery] = useState('');

  // O(N) — memoized flat list of all non-linked menu items
  const items = useMemo(
    () =>
      menuCardList
        ? Object.values(menuCardList)
            .flatMap(obj => Object.values(obj))
            .filter(data => !data.linkedItem)
        : [],
    [menuCardList]
  );

  const fuse = useMemo(() => new Fuse(items, FUSE_OPTIONS), [items]);

  const results = useMemo(() => fuse.search(query), [fuse, query]);

  const characterResults = useMemo(() => {
    if (query.length < 2) return [];
    return results.map((character) => ({ data: character.item, score: character.score ?? 1 })) || [];
  }, [query, results]);
  
  const getWordMatchPos = (text, q) => {
    if (!text) return Infinity;
    const idx = text.toLowerCase().split(/\W+/).findIndex(w => w.startsWith(q));
    return idx === -1 ? Infinity : idx;
  };
  
  // Best (lowest) word-match position across an array of string values
  const getBestWordPos = (values, q) =>
    Math.min(...(values || []).map(v => getWordMatchPos(v, q)));
  
  const deduplicateResults = useMemo(() => {
    const unique = characterResults.filter(
      (item, index, self) =>
        index === self.findIndex(i => i.data.menuItemId === item.data.menuItemId)
    );
  
    const q = query.toLowerCase();
  
    return unique.sort((a, b) => {
      const aItem = a.data.menuItem;
      const bItem = b.data.menuItem;
  
      // GROUP: does the item name contain the query at all?
      const aInName = (aItem?.name?.toLowerCase() || "").includes(q);
      const bInName = (bItem?.name?.toLowerCase() || "").includes(q);
  
      // Group 1 (name match) always beats Group 2 (keyword/alias/abbreviation match)
      if (aInName !== bInName) return aInName ? -1 : 1;
  
      if (aInName) {
        // Within name group → rank by which word starts with the query (earlier = better)
        const aNamePos = getWordMatchPos(aItem?.name, q);
        const bNamePos = getWordMatchPos(bItem?.name, q);
        if (aNamePos !== bNamePos) return aNamePos - bNamePos;
      } else {
        // Within other-fields group → rank by earliest word-start in aliases/keywords/abbreviations
        const aOtherPos = Math.min(
          getBestWordPos(aItem?.aliases, q),
          getBestWordPos(aItem?.keywords, q),
          getBestWordPos(aItem?.abbreviations, q),
        );
        const bOtherPos = Math.min(
          getBestWordPos(bItem?.aliases, q),
          getBestWordPos(bItem?.keywords, q),
          getBestWordPos(bItem?.abbreviations, q),
        );
        if (aOtherPos !== bOtherPos) return aOtherPos - bOtherPos;
      }
      return a.score - b.score;
    }).map(ranked => ranked.data);
  }, [characterResults, query]);

  // Build idToName lookup once from menu screens
  const idToName = useMemo(() => {
    const defaultMenus = menusList.flatMap(x => x.screens);
    return defaultMenus.reduce((acc, screen) => {
      screen.menuItemLinks?.filter(x => !x.linkedItem)?.forEach(link => {
        acc[link.menuItemId] = screen.name;
      });
      return acc;
    }, {});
  }, [menusList]);

  // O(1) per render item — replace O(N²) categoryName.map loop
  const categoryMap = useMemo(() => {
    const map = {};
    deduplicateResults.forEach(item => {
      map[item.menuItemId] = idToName[item.menuItemId] || null;
    });
    return map;
  }, [deduplicateResults, idToName]);

  const onSearchChange = (currentTarget) => {
    updateQuery(currentTarget);
  };

  const menuItemClick = useCallback(
    (data) => {
      const { menuTypeId, menuItemId } = data;
      const currentMenuId = menusList[menuIndex]?.id;

      const requiredModLength = checkRequiredModifierCount({
        menuItemId,
        menuId: currentMenuId,
        menuItemsHashTable,
        modifierGroupsHashTable,
      });

      if (requiredModLength === 0) {
        // No required modifiers — add directly to cart
        const itemInfo = menuCardList[menuTypeId][menuItemId];
        const dataToSend = {
          quantity: 1,
          price: data.price || itemInfo?.price,
          menuItemId,
          menuTypeId,
          brandCourseId: itemInfo?.menuItem?.brandCourseId,
          modifiers: renderInitialData({
            menuItemsHashTable,
            modifierGroupsHashTable,
            modifiersHashTable,
            activeMenuItemId: 0,
            menuItemId,
          }),
          note: null,
          menuItemName: itemInfo?.menuItem?.name,
          serverAppModifierGroupDisplayStyle:
            data?.serverAppModifierGroupDisplayStyle ?? 0,
        };

        let cartItem = updateExcludeModifier({
          cartItemsList: [dataToSend],
          menuItemsHashTable,
          modifierGroupsHashTable,
          modifiersHashTable,
        });
        cartItem = applyModFunction({ cartItemsList: cartItem, modifierGroupsHashTable });

        const modifierObject = getObjectValue(
          () =>
            mapStringCartToObject(
              cartItem,
              [],
              modifiersHashTable,
              modifierGroupsHashTable
            )[0].modifiers,
          []
        );
        dataToSend.modifierObject = modifierObject.filter(e => e.modifierGroupId);

        const modiferPrice = calculateModifierAmount({
          modifierData: [],
          modifierGroupsHashTable,
        });
        const position = getObjectValue(
          () => JSON.parse(window.sessionStorage.getItem('position')),
          {}
        );
        const requestData = {
          ...dataToSend,
          pending: true,
          seatIds: position?.number === -1 ? [] : [position?.seatId],
          price: Number(dataToSend.price) + modiferPrice,
          menuScreenId: Number(menuTypeId),
          internalId: new Date().getTime(),
          menuId: currentMenuId,
          modifiers: dataToSend.modifierObject,
          modifierData: dataToSend.modifiers,
        };
        requestData.modifierJson = JSON.stringify({ ...requestData, openPrice: false });

        props.addToCart({ ...requestData, isProcessing: true, openPrice: false });
        setSearchModalOpen(false);
      } else {
        // Has required modifiers — navigate to order detail screen
        const modifierList = menuItemsHashTable[menuItemId].modifierGroupLinks.map(
          item => ({
            ...item,
            ...modifierGroupsHashTable[item.modifierGroupId],
          })
        );
        props.pushModifiersGroupData(modifierList).then(() => {
          window.sessionStorage.setItem(
            'serverAppModifierGroupDisplayStyle',
            data?.serverAppModifierGroupDisplayStyle ?? 0
          );
          navigate(
            `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${menuItemId}?menuId=${currentMenuId}&orderType=order`
          );
          setSearchModalOpen(false);
        });
      }
    },
    [
      menuCardList,
      menuItemsHashTable,
      modifierGroupsHashTable,
      modifiersHashTable,
      menusList,
      menuIndex,
      navigate,
      setSearchModalOpen,
      props.addToCart,
      props.pushModifiersGroupData,
    ]
  );

  return (
    <div className="h-auto">
      {loading && <Loader />}
      <div className={cn('modal', isOpen ? 'fade show d-block' : 'fade', customClass)}>
        <div className={cn(customClass2)} role="document">
          <div style={style}>
            <Search
              placeholderName={LABEL().SEARCH_ITEM}
              orderSearch
              onChange={onSearchChange}
              onClose={() => { setSearchModalOpen(false); }}
              isTop
              browserOrtablet={browserOrTablet}
              setOpenKeyboard={setOpenKeyboard}
              openKeyboard={openKeyboard}
              serverAppTheme={serverAppTheme}
              checkList
            />
            <div className={cn('d-flex flex-wrap py-2', Styles.scroll)}>
              {deduplicateResults.map((item, idx) => (
                <div
                  className={Styles.foodGrid}
                  key={`${item.menuItem.name}-${item.menuItemId}-${idx}`}
                >
                  <div
                    className={cn(
                      Styles.foodItem,
                      item.menuItem?.outOfStock ? Styles.searchButtonOutOfStock : ''
                    )}
                    role="button"
                    onClick={() => !item.menuItem?.outOfStock && menuItemClick(item)}
                  >
                    {item.menuItem?.outOfStock && (
                      <div className={Styles.outOfStockNumber}>86</div>
                    )}
                    <span className={Styles.foodName}>{item.menuItem.name}</span>
                    <span className={Styles.foodCategory}>
                      {categoryMap[item.menuItemId] || ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

OrderMenuSearchModal.propTypes = {
  isOpen: PropTypes.bool,
  setSearchModalOpen: PropTypes.func,
  customClass: PropTypes.string,
  customClass2: PropTypes.string,
  loading: PropTypes.bool,
  style: PropTypes.object,
  serverAppTheme: PropTypes.object,
  browserOrTablet: PropTypes.bool,
  setOpenKeyboard: PropTypes.func,
  openKeyboard: PropTypes.bool,
  menusList: PropTypes.array,
};

OrderMenuSearchModal.defaultProps = {
  isOpen: false,
  setSearchModalOpen: () => {},
  customClass: '',
  customClass2: '',
  loading: false,
  style: {},
  serverAppTheme: {},
  browserOrTablet: false,
  setOpenKeyboard: () => {},
  openKeyboard: false,
  menusList: [],
};

const mapStateToProps = ({ orders }) => ({
  loading: orders.loading,
  menuCardList: orders.menuCardList,
  menusList: orders.menusList,
  menuIndex: orders.menuIndex,
  menuItemsHashTable: orders.menuItemsHashTable,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  modifiersHashTable: orders.modifiersHashTable,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ addToCart, pushModifiersGroupData }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(memo(OrderMenuSearchModal));
